import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useExtensionDetected } from "../../hooks/useExtensionDetected";

const CHROME_STORE_URL =
  "https://chromewebstore.google.com/detail/duneworks-ai/lleekikobhbnleadhcjmfjpiflcpehmc?utm_source=gmail&utm_medium=email&utm_campaign=early_access_launch";

export function ExtensionNudge() {
  const extensionInstalled = useExtensionDetected();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || extensionInstalled) return null;

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleInstall = () => {
    window.open(CHROME_STORE_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.4, ease: [0.22, 0.68, 0, 1.2] }}
        className="relative mt-4 w-full max-w-[500px] overflow-hidden rounded-[14px] bg-[#FAF5EE] dark:bg-[#0E1A2D] border border-[#E8D5B8] dark:border-[#2C405F] p-[13px_15px] flex items-center gap-3"
      >
        {/* Shine sweep */}
        <div className="pointer-events-none absolute inset-0 animate-[nudge-shine_3s_ease-in-out_1s_infinite]">
          <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-[rgba(255,186,85,0.05)] to-transparent" />
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="absolute top-[7px] right-[9px] border-none bg-transparent text-[#B7AB9E] dark:text-[#6E829E] text-[16px] leading-none cursor-pointer p-[2px_4px] hover:text-[#6A5F54] dark:hover:text-[#9AAABF] transition-colors"
          aria-label="Dismiss"
        >
          ×
        </button>

        {/* Icon */}
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border border-[#E8D5B8] dark:border-[#2C405F] bg-[#F5EFE6] dark:bg-[#1A2D47]">
          <svg viewBox="0 0 20 20" fill="none" className="h-[17px] w-[17px]">
            <rect
              x="2.5"
              y="5.5"
              width="15"
              height="9"
              rx="2"
              stroke="#E8922A"
              strokeWidth="1.4"
            />
            <path
              d="M6.5 5.5V4.5A1 1 0 017.5 3.5h5a1 1 0 011 1v1"
              stroke="#E8922A"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <circle cx="10" cy="10" r="1.5" fill="#E8922A" />
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-semibold tracking-[0.07em] text-[#C47F2E] dark:text-[#C47F2E] uppercase mb-[2px]"
            style={{ fontFamily: "Sofia Pro, sans-serif" }}
          >
            Chrome Extension
          </div>
          <div
            className="text-[12.5px] font-bold text-[#1D1B19] dark:text-[#E3E6EA] leading-[1.3] mb-[2px]"
            style={{ fontFamily: "Sofia Pro, sans-serif" }}
          >
            Your autonomous AI twin — smarter decisions, less effort.
          </div>
          <div
            className="text-[10.5px] text-[#8E8378] dark:text-[#6E829E]"
            style={{ fontFamily: "Sofia Pro, sans-serif" }}
          >
            Point it at any problem · get context · act with confidence
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleInstall}
          className="relative flex flex-shrink-0 items-center gap-[6px] overflow-hidden whitespace-nowrap rounded-lg bg-[#F0E8D8] dark:bg-[#E3E6EA] px-[11px] py-[7px] border-none cursor-pointer transition-colors hover:bg-[#E8DEC8] dark:hover:bg-[#D1D5DB]"
        >
          {/* CTA shine */}
          <div className="pointer-events-none absolute inset-0 animate-[nudge-shine_2.6s_ease-in-out_0.8s_infinite]">
            <div className="absolute top-0 -left-full h-full w-3/5 bg-gradient-to-r from-transparent via-[rgba(232,146,42,0.18)] to-transparent" />
          </div>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            className="relative z-[1] h-4 w-4 flex-shrink-0"
          >
            <circle cx="12" cy="12" r="4.5" fill="#1A73E8" />
            <path d="M12 7.5h9.03a10.5 10.5 0 00-9.03-5.25" fill="#EA4335" />
            <path
              d="M3.27 17.25L7.8 9.38A10.5 10.5 0 012.97 17.25"
              fill="#FBBC05"
            />
            <path
              d="M7.8 9.38A10.5 10.5 0 0121.03 7.5H12a4.5 4.5 0 00-4.2 1.88z"
              fill="#EA4335"
            />
            <path
              d="M12 16.5a4.5 4.5 0 01-4.2-7.12L3.27 17.25a10.5 10.5 0 0017.46.75L16.5 12a4.5 4.5 0 01-4.5 4.5z"
              fill="#34A853"
            />
            <path
              d="M20.73 18a10.5 10.5 0 00.3-.75L16.5 12a4.5 4.5 0 01-4.5 4.5 4.5 4.5 0 004.23 2.98A10.5 10.5 0 0020.73 18z"
              fill="#FBBC05"
            />
          </svg>
          <span
            className="relative z-[1] text-[11px] font-bold text-[#1A1208]"
            style={{ fontFamily: "Sofia Pro, sans-serif" }}
          >
            Add to Chrome
          </span>
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
